import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import Link from 'components/Link'
import Spinner from 'components/Spinner'
import {
  DateCell,
  TextCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHost,
  TableRow,
  TableScrollingContainer,
} from 'components/Table'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'
import { formatMoney } from 'utils/money-util'
import { CallCenterMembershipItem } from 'call-center/CallCenterSelectionViewModel'
import { CallCenterContact } from 'call-center/models/CallCenterContact'
import { CallCenterJobSummary } from 'call-center/models/CallCenterJobSummary'
import { AddToWorkspaceOptions, InviteToWorkspaceOptions } from '../types'
import styles from './ClientDetails.sass'
import { JobStatus } from 'jobs/dtos/job'
import { LegendVersionId, LegendServiceType } from 'legends/PublishedLegend'
import link from 'infra/link'
import { match } from 'utils/pattern-match-util'
import { CallCenterCustomerLookupViewModel } from 'call-center/CallCenterCustomerLookupViewModel'

interface ClientDetailsProps {
  vm: CallCenterCustomerLookupViewModel
  onInviteToWorkspace: (options: InviteToWorkspaceOptions) => void
  onAddToWorkspace: (options: AddToWorkspaceOptions) => void
  onCreateJob: (membership: CallCenterMembershipItem) => void
}

export default observer(function ClientDetails({
  vm,
  onInviteToWorkspace,
  onAddToWorkspace,
  onCreateJob,
}: ClientDetailsProps) {
  const result = vm.callCenterSelectionVM.selectedResult

  if (!result) {
    return null
  }

  const contact = result.hubspotContact ?? null
  const primaryMember = result.workspaceMembers[0]?.member ?? null

  const fullName = contact
    ? [contact.firstName, contact.lastName].filter(Boolean).join(' ')
    : (primaryMember?.displayName ?? '')

  const email = contact?.email || primaryMember?.email || ''
  const phone = contact?.phone || primaryMember?.phone || ''

  const membershipItems = vm.callCenterSelectionVM.membershipItems
  const membershipEntries = membershipItems.filter(
    (item) => item.type === 'membership',
  )
  const waitlistEntries = membershipItems.filter(
    (item) => item.type === 'waitlist',
  )
  const shouldShowEmptyMembership =
    membershipEntries.length === 0 && waitlistEntries.length === 0

  const handleAddToWorkspace = () => {
    onAddToWorkspace({
      email,
      firstName: contact?.firstName ?? primaryMember?.givenName ?? '',
      lastName: contact?.lastName ?? primaryMember?.familyName ?? '',
      phone: phone || undefined,
    })
  }

  return (
    <div className={styles.detailsContainer}>
      <ContactCard fullName={fullName} email={email} phone={phone} />
      <MembershipCard
        membershipEntries={membershipEntries}
        waitlistEntries={waitlistEntries}
        shouldShowEmptyMembership={shouldShowEmptyMembership}
        contact={contact}
        onInviteToWorkspace={onInviteToWorkspace}
        onAddToWorkspace={handleAddToWorkspace}
        onCreateJob={onCreateJob}
      />
      <JobHistoryCard vm={vm} />
    </div>
  )
})

const ContactCard = ({
  fullName,
  email,
  phone,
}: {
  fullName: string
  email: string
  phone: string
}) => (
  <div className={styles.sectionCard}>
    <div className={styles.sectionCardTitle}>Contact</div>
    <div className={styles.contactGrid}>
      <ContactField label="Name" value={fullName || '—'} />
      <ContactField label="Email" value={email || '—'} />
      <ContactField label="Phone" value={phone || '—'} />
    </div>
  </div>
)

const MembershipCard = ({
  membershipEntries,
  waitlistEntries,
  shouldShowEmptyMembership,
  contact,
  onInviteToWorkspace,
  onAddToWorkspace,
  onCreateJob,
}: {
  membershipEntries: CallCenterMembershipItem[]
  waitlistEntries: CallCenterMembershipItem[]
  shouldShowEmptyMembership: boolean
  contact: CallCenterContact | null
  onInviteToWorkspace: (options: InviteToWorkspaceOptions) => void
  onAddToWorkspace: () => void
  onCreateJob: (membership: CallCenterMembershipItem) => void
}) => (
  <div className={styles.sectionCard}>
    <div className={styles.sectionCardTitle}>Workspace memberships</div>
    {shouldShowEmptyMembership ? (
      <div className={styles.jobsEmpty}>No workspace memberships found.</div>
    ) : (
      <div className={styles.membershipsTableContainer}>
        <TableHost>
          <TableScrollingContainer>
            <Table striped>
              <TableHead>
                <TableHeader alignment="left">Workspace</TableHeader>
                <TableHeader alignment="left">Last login</TableHeader>
                <TableHeader alignment="right">Actions</TableHeader>
              </TableHead>
              <TableBody>
                {membershipEntries.map((item) => (
                  <MembershipItem
                    key={item.key}
                    item={item}
                    onCreateJob={onCreateJob}
                  />
                ))}
                {contact &&
                  waitlistEntries.map((item) => (
                    <WaitlistItem
                      key={item.key}
                      item={item}
                      contact={contact}
                      onInviteToWorkspace={onInviteToWorkspace}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableScrollingContainer>
        </TableHost>
      </div>
    )}
    <div className={styles.membershipCardFooter}>
      <Button type="primary" size="normal" onClick={onAddToWorkspace}>
        Add to workspace
      </Button>
    </div>
  </div>
)

const isJobNavigable = (
  job: CallCenterJobSummary,
  vm: CallCenterCustomerLookupViewModel,
): boolean => {
  if (job.status !== 'UNDER_CONSTRUCTION') {
    return false
  }

  const legend = vm.publishedLegendStore.get(
    LegendVersionId(job.legendId, job.legendVersion),
  )

  return legend.serviceType === LegendServiceType.Tax
}

const JobHistoryCard = observer(function JobHistoryCard({
  vm,
}: {
  vm: CallCenterCustomerLookupViewModel
}) {
  const jobs = vm.callCenterJobHistoryVM.jobs
  const scroller = vm.callCenterJobHistoryVM.jobsScroller
  const hasNoJobs =
    jobs.length === 0 && scroller.isDone && !scroller.fetch.pending

  if (scroller.fetch.error) {
    return (
      <div className={styles.sectionCard}>
        <div className={styles.sectionCardTitle}>Job history</div>
        <div className={styles.jobsError}>
          Unable to load job history. Please try again.
        </div>
      </div>
    )
  }

  if (hasNoJobs) {
    return (
      <div className={styles.sectionCard}>
        <div className={styles.sectionCardTitle}>Job history</div>
        <div className={styles.jobsEmpty}>No jobs found for this client.</div>
      </div>
    )
  }

  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionCardTitle}>Job history</div>
      <div className={styles.jobsTableContainer}>
        <TableHost>
          <TableScrollingContainer>
            <Table striped>
              <TableHead>
                <TableHeader alignment="left">Name</TableHeader>
                <TableHeader alignment="left">Workspace</TableHeader>
                <TableHeader alignment="left">Status</TableHeader>
                <TableHeader alignment="left">Date created</TableHeader>
                <TableHeader alignment="left">Specs</TableHeader>
                <TableHeader alignment="right">Total</TableHeader>
                <TableHeader alignment="center">Actions</TableHeader>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TextCell>{job.name}</TextCell>
                    <TextCell>{job.workspace.name}</TextCell>
                    <TextCell>{mapJobStatusToLabel(job.status)}</TextCell>
                    <DateCell date={job.dateCreated} />
                    <TextCell>{job.jobScopeItems.join(', ')}</TextCell>
                    <TextCell alignment="right">
                      {job.total !== undefined ? formatMoney(job.total) : '—'}
                    </TextCell>
                    <TextCell alignment="center">
                      {isJobNavigable(job, vm) && job.id && (
                        <Link to={link.callCenterJob(job.id)}>
                          <Button type="primary" size="small">
                            Go to Job
                          </Button>
                        </Link>
                      )}
                    </TextCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!scroller.isDone && (
              <InfiniteScrollLoader
                scrollProvider={vm.callCenterJobHistoryVM.jobsScroller}
                compact
              />
            )}
          </TableScrollingContainer>
        </TableHost>
      </div>
    </div>
  )
})

const ContactField = ({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) => (
  <div>
    <div className={styles.contactFieldLabel}>{label}</div>
    <div className={styles.contactFieldValue}>{value || '—'}</div>
  </div>
)

const MembershipItem = ({
  item,
  onCreateJob,
}: {
  item: CallCenterMembershipItem
  onCreateJob: (membership: CallCenterMembershipItem) => void
}) => {
  const workspaceName = item.workspace?.name || `Workspace ${item.workspaceId}`
  const canCreateJob = Boolean(item.workspace && item.member)

  return (
    <TableRow>
      <TableCell>
        <div className={styles.workspaceCell}>
          {workspaceName}
          {item.isWaitlistWorkspace && (
            <span className={styles.memberBadge}>Signed up for waitlist</span>
          )}
        </div>
      </TableCell>
      <DateCell date={item.member?.lastSeen ?? null} />
      <TableCell>
        <div className={styles.actionCell}>
          <Button
            type="default"
            size="small"
            disabled={!canCreateJob}
            onClick={() => onCreateJob(item)}
          >
            Create job
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

interface WaitlistItemProps {
  item: CallCenterMembershipItem
  contact: CallCenterContact
  onInviteToWorkspace: (options: InviteToWorkspaceOptions) => void
}

const WaitlistItem = ({
  item,
  contact,
  onInviteToWorkspace,
}: WaitlistItemProps) => {
  const workspaceName = item.workspace?.name || `Workspace ${item.workspaceId}`

  const handleInvite = () => {
    onInviteToWorkspace({
      contact,
      workspaceId: item.workspaceId,
      workspace: item.workspace,
    })
  }

  const description = item.loading
    ? 'Processing...'
    : item.error
      ? item.error
      : 'This contact signed up for the waitlist in this workspace.'

  return (
    <TableRow>
      <TableCell>
        <div className={styles.workspaceCell}>
          <div>
            <div>{workspaceName}</div>
            <div className={styles.waitlistDescription}>
              {item.loading && (
                <span className={styles.waitlistLoading}>
                  <Spinner size={16} />
                </span>
              )}
              {'* ' + description}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell />
      <TableCell>
        <div className={styles.actionCell}>
          <Button
            type="default"
            size="small"
            onClick={handleInvite}
            disabled={!contact.email || item.loading}
          >
            Add to workspace
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

function mapJobStatusToLabel(status: JobStatus): string {
  return match(status, {
    UNDER_CONSTRUCTION: () => 'Under Construction',
    INFO_GATHERING: () => 'Info Gathering',
    UNCLAIMED: () => 'Unclaimed',
    CLAIMED: () => 'Claimed',
    ON_HOLD: () => 'On Hold',
    IDLE: () => 'Idle',
    CLOSED: () => 'Closed',
  })
}
